import { useQuery } from '@tanstack/react-query'
import { apiClient } from './client'
import { useAuth } from '../hooks/useAuth'
import type {
  AnalyticsResponse,
  AnalyticsQueryParams,
  RequestsResponse,
  RequestsQueryParams,
  RequestDetailResponse,
} from '../types/analytics'

export function useAnalytics(params: AnalyticsQueryParams) {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['analytics', params],
    queryFn: () =>
      apiClient.get<AnalyticsResponse>('/v1/analytics', {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.aggregation && { aggregation: params.aggregation }),
      }),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useAnalyticsRequests(params: RequestsQueryParams) {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['analytics-requests', params],
    queryFn: () =>
      apiClient.get<RequestsResponse>('/v1/analytics/requests', {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.verdict && { verdict: params.verdict }),
        ...(params.file_extension && { file_extension: params.file_extension }),
        ...(params.limit && { limit: params.limit.toString() }),
        ...(params.cursor && { cursor: params.cursor }),
      }),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useLimits() {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['limits'],
    queryFn: () => apiClient.get<LimitsResponse>('/v1/limits'),
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useUsage() {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['usage'],
    queryFn: () => apiClient.get<UsageResponse>('/v1/usage'),
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
  })
}

// Types for existing endpoints
export interface LimitsResponse {
  plan: {
    id: number
    name: string
  }
  limits: {
    daily: {
      limit: number
      used: number
      remaining: number
      resets_at: string  // ISO 8601 timestamp
    }
    per_minute: number
    max_lines: number
    max_voters: number
  }
  overage: {
    allowed: boolean
    cost_cents: number
  }
}

interface UsageResponse {
  daily_usage: number
  total_usage: number
}

export function useRequestDetail(requestId: string | null) {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['request-detail', requestId],
    queryFn: () => apiClient.get<RequestDetailResponse>(`/v1/analytics/requests/${requestId}`),
    enabled: isAuthenticated && requestId !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
